// Editing-side helpers for the super-admin sales-agent catalog settings on the chat bot:
// chatBot.agentFieldMapping (which product custom fields the agent reads and searches),
// chatBot.priceDisplayRule (when a product's price is not listed) and chatBot.agentPlaybook (the
// "Playbook" instructions). Bounds mirror the API, which validates everything again —
// these only let a super admin see a problem before a 400, same posture as helpers/knowledgeIngest.js.

export const MAX_MAPPING_ROWS = 12;
export const MAX_CONTEXT_ROWS = 8;
export const MAX_CARD_ROWS = 2;
export const MAX_LABEL_LENGTH = 40;
export const MAX_TEMPLATE_LENGTH = 1000;
export const MAX_BOOST_VALUES = 5;
// The API's per-value limit (Elasticsearch ignore_above on custom.string.values).
export const MAX_BOOST_VALUE_LENGTH = 256;
export const BUILT_IN_KEY = 'categories';

export const ROLES = [
  { value: 'context', label: 'Context' },
  { value: 'searchable', label: 'Searchable' },
  // Shown on product cards when Card fields is on.
  { value: 'card', label: 'Card' },
  // A catalog count for an exact value the shopper names; text fields only, and only with Context.
  { value: 'count', label: 'Count' },
];

const isBuiltIn = (row) => row?.source === 'builtIn';

/** A custom row's field type; rows saved before number fields carry none and are text fields. */
export const mappingRowType = (row) => (!isBuiltIn(row) && row?.type === 'numeric' ? 'numeric' : 'string');

/** A custom text row: the only kind that takes Boost values and the Count role. */
export const isCustomTextRow = (row) => !isBuiltIn(row) && mappingRowType(row) === 'string';

/** A row with neither Context nor Card (Searchable-only or boost-only): it reaches no template. */
export const reachesNoTemplate = (row) => !row?.roles?.some((role) => role === 'context' || role === 'card');

export const builtInRow = () => ({ source: 'builtIn', key: BUILT_IN_KEY, label: BUILT_IN_KEY, roles: ['searchable'] });
export const blankMappingRow = () => ({ source: 'custom', key: '', label: '', roles: ['context'] });

export const hasBuiltInRow = (rows) => (rows || []).some(isBuiltIn);

/** `rows` with the built-in categories row present (`on`) or absent; it always goes first. */
export const toggleBuiltInRow = (rows, on) => {
  const custom = (rows || []).filter((row) => !isBuiltIn(row));
  return on ? [builtInRow(), ...custom] : custom;
};

const trimmed = (value) => (typeof value === 'string' ? value.trim() : '');
const cleanValues = (values) => (Array.isArray(values) ? values.map(trimmed).filter(Boolean) : []);

/** A row's Boost values as the API takes them: trimmed, non-empty, distinct. */
const boostValues = (row) => [...new Set(cleanValues(row?.boost))];

const roleCount = (rows, role) => (rows || []).filter((row) => row?.roles?.includes(role)).length;
export const contextRowCount = (rows) => roleCount(rows, 'context');
export const cardRowCount = (rows) => roleCount(rows, 'card');

// The API's identity for a row: source + type + key, so a text and a number field may share a key.
const mappingRowId = (row) => `${row?.source}:${mappingRowType(row)}:${trimmed(row?.key)}`;

/** Keys used by more than one row of the same source and type (exact, as the API compares). */
export const duplicateMappingKeys = (rows) => {
  const seen = new Set();
  const duplicates = new Set();
  (rows || []).forEach((row) => {
    const id = mappingRowId(row);
    if (!trimmed(row?.key)) return;
    if (seen.has(id)) duplicates.add(id);
    seen.add(id);
  });
  return duplicates;
};

/** Per-field problems of one row: { key, label, roles, template, boost } (null = fine). */
export const mappingRowErrors = (row, duplicates = new Set()) => {
  const key = trimmed(row?.key);
  const label = trimmed(row?.label);
  const roles = Array.isArray(row?.roles) ? row.roles : [];
  const template = trimmed(row?.template);
  const boost = boostValues(row);
  const text = isCustomTextRow(row);
  let keyError = null;
  if (!key) keyError = 'Pick a field';
  else if (isBuiltIn(row) && key !== BUILT_IN_KEY) keyError = `Only ${BUILT_IN_KEY} is built in`;
  else if (duplicates.has(mappingRowId(row))) keyError = 'Another row uses this field';
  let labelError = null;
  if (!label) labelError = 'Enter a label';
  else if (label.length > MAX_LABEL_LENGTH) labelError = `At most ${MAX_LABEL_LENGTH} characters`;
  let rolesError = null;
  if (roles.length === 0 && boost.length === 0)
    rolesError = text ? 'Tick a role or add Boost values' : 'Tick Context, Searchable or Card';
  else if (isBuiltIn(row) && (roles.length !== 1 || roles[0] !== 'searchable'))
    rolesError = `${BUILT_IN_KEY} can only be searchable`;
  else if (mappingRowType(row) === 'numeric' && roles.includes('searchable'))
    rolesError = 'Number fields cannot be Searchable';
  else if (roles.includes('count') && !text) rolesError = 'Only text fields can Count';
  else if (roles.includes('count') && !roles.includes('context')) rolesError = 'Count needs Context';
  let templateError = null;
  if (template.length > MAX_TEMPLATE_LENGTH) templateError = `At most ${MAX_TEMPLATE_LENGTH} characters`;
  else if (template && reachesNoTemplate(row)) templateError = 'Only a Context or Card field has a template';
  let boostError = null;
  if (boost.length && !text) boostError = 'Only text fields take Boost values';
  else if (boost.length > MAX_BOOST_VALUES) boostError = `At most ${MAX_BOOST_VALUES} Boost values`;
  else if (boost.some((value) => value.length > MAX_BOOST_VALUE_LENGTH))
    boostError = `Boost values are at most ${MAX_BOOST_VALUE_LENGTH} characters`;
  return { key: keyError, label: labelError, roles: rolesError, template: templateError, boost: boostError };
};

/** First reason `rows` can't be saved, or null. */
export const validateAgentFieldMapping = (rows) => {
  if (!Array.isArray(rows)) return 'Agent catalog fields are missing';
  if (rows.length > MAX_MAPPING_ROWS) return `At most ${MAX_MAPPING_ROWS} fields`;
  if (contextRowCount(rows) > MAX_CONTEXT_ROWS) return `At most ${MAX_CONTEXT_ROWS} Context fields`;
  if (cardRowCount(rows) > MAX_CARD_ROWS) return `At most ${MAX_CARD_ROWS} Card fields`;
  const duplicates = duplicateMappingKeys(rows);
  // An unnamed row is numbered among the custom rows only, as the editor lists them.
  const customRows = rows.filter((row) => !isBuiltIn(row));
  for (const row of rows) {
    const message = Object.values(mappingRowErrors(row, duplicates)).find(Boolean);
    if (message) return `Field ${trimmed(row?.key) || customRows.indexOf(row) + 1}: ${message}`;
  }
  return null;
};

/**
 * Rows as the API takes them: { source, key, label, roles } trimmed, plus `type` on every custom
 * row (never on the built-in one), `template` only when it has text and `boost` only when it has values.
 */
export const toAgentFieldMappingPayload = (rows) =>
  rows.map((row) => {
    const { source, key, label, roles } = row;
    const payload = { source, key: key.trim(), label: label.trim(), roles: [...roles] };
    if (!isBuiltIn(row)) payload.type = mappingRowType(row);
    const template = trimmed(row.template);
    if (template) payload.template = template;
    const boost = boostValues(row);
    if (boost.length) payload.boost = boost;
    return payload;
  });

export const blankPriceDisplayRule = () => ({ hideWhen: { key: '', values: [] } });

/** Per-field problems of a rule: { key, values } (null = fine); a null rule has none. */
export const priceDisplayRuleErrors = (rule) => {
  if (rule === null || rule === undefined) return { key: null, values: null };
  const key = trimmed(rule?.hideWhen?.key);
  return {
    key: key ? null : 'Pick a field',
    values: cleanValues(rule?.hideWhen?.values).length ? null : 'Add at least one value',
  };
};

/** First reason `rule` can't be saved, or null. */
export const validatePriceDisplayRule = (rule) => {
  const message = Object.values(priceDisplayRuleErrors(rule)).find(Boolean);
  return message ? `Hide price when: ${message}` : null;
};

/** The rule as the API takes it: exactly { hideWhen: { key, values } } trimmed, or null. */
export const toPriceDisplayRulePayload = (rule) =>
  rule ? { hideWhen: { key: rule.hideWhen.key.trim(), values: cleanValues(rule.hideWhen.values) } } : null;

export const MAX_PLAYBOOK_LENGTH = 3000;
// The length meter: dimmed up to the first, a warning up to the second, an error above it.
export const PLAYBOOK_WARN_LENGTH = 1500;
export const PLAYBOOK_ERROR_LENGTH = 2500;

/** Length as the API counts it: code points of the trimmed text. */
export const playbookLength = (text) => [...trimmed(text)].length;

/** First reason `text` can't be saved, or null. */
export const validateAgentPlaybook = (text) =>
  playbookLength(text) > MAX_PLAYBOOK_LENGTH ? `Playbook: At most ${MAX_PLAYBOOK_LENGTH} characters` : null;

/** The playbook as the API takes it: trimmed, and null when empty. */
export const toAgentPlaybookPayload = (text) => trimmed(text) || null;
